// React
import React, { useEffect, useState } from 'react';

// FutureLab
import CompanyLogo from '../../common/CompanyLogo';
import { returnOneToOneChatUserDetails } from '../../../utilities/UserDetails';
import { ProfilePreviewData } from '../../popup/userDetails';
import { useLazyGetUserInfoQuery } from '../../../store/api/userApi';
import { ModalConfirmation } from '../../popup/modalConfirmation';
import MobileChatTopNavBar from './MobileChatTopNavBar';

// UI
import { Home, LogOut, Menu } from 'lucide-react';
import { Button } from '../../ui/button';
import { Separator } from '../../ui/separator';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../../ui/sheet';

// Redux
import { useSelector } from 'react-redux';
import { RootState } from '../../../reducers';

// 3rd Party
import _ from 'lodash';
import { getUserDisplayName } from '../../../utilities/UserDetails';

interface MobileTopNavBarProps {
	logout: () => void;
	token: string;
}

export default function MobileTopNavBar(props: MobileTopNavBarProps) {
	const { logout, token } = props;
	const [menuOpen, setMenuOpen] = useState(false);
	const chatInFocus = useSelector((state: RootState) => state.chatInFocus);
	const loggedInUserDetails = useSelector((state: RootState) => state.loggedInUserDetails);
	const [loggedInUserProfileDetails, setLoggedInUserProfileDetails] = useState<ProfilePreviewData>();
	const [loadUserInfo] = useLazyGetUserInfoQuery();
  const [displayModalConfirmation, setDisplayModalConfirmation] = useState<boolean>(false);
	const photoUrl = `${process.env.REACT_APP_PROFILE_PHOTOS_BASE_URL}${process.env.REACT_APP_PROFILE_PHOTOS_S3_BASE_URL}${process.env.REACT_APP_PROFILE_PHOTOS_S3_DIRECTORY_URL}`;
	const userListProfilesDetails = useSelector((state: RootState) => state.userListProfilesDetails);
	const userProfileDetails = returnOneToOneChatUserDetails(chatInFocus, loggedInUserDetails, userListProfilesDetails);
	const loggedInUserFullName = getUserDisplayName(loggedInUserProfileDetails, 'FutureLab member');
  const optionStore = { action: 'leavingFL' };

	useEffect(() => {
		const loggedInUser = loggedInUserDetails;
		if (!_.isEmpty(loggedInUser) && token.length > 0) {
			loadUserInfo({ userId: loggedInUserDetails.id })
				.unwrap()
				.then((user) => setLoggedInUserProfileDetails(user as ProfilePreviewData))
				.catch((error) => console.warn('Unable to load account profile:', error));
		}
	}, [loadUserInfo, loggedInUserDetails, token]);

	const handleClose = () => setMenuOpen(false);

  const navigateToOrigin = () => {
		const originUrl = localStorage.getItem('originURL');
		if (originUrl) {
			window.open(`${process.env.REACT_APP_PORTAL_BASE_URL}${originUrl}/overviews`, '_self');
		} else {
			window.open(`${process.env.REACT_APP_B2C_BASE_URL}main/dashboard`, '_self');
		}

		setDisplayModalConfirmation(!displayModalConfirmation);
    localStorage.removeItem('originURL');
	};

	return (
		<div style={{ fontFamily: 'DM Sans' }}>
			{!!chatInFocus?.id ? (
				<MobileChatTopNavBar
					chatInFocus={chatInFocus}
					loggedInUserDetails={loggedInUserDetails}
					userListProfilesDetails={userListProfilesDetails}
					photoUrl={photoUrl}
					userProfileDetails={userProfileDetails}
				/>
			) : (
				<div className="mobile-navbar-top-container fixed inset-x-0 top-0 z-40 flex h-[60px] w-full min-w-0 bg-primary shadow-sm">
					<section className="flex h-full min-w-0 items-center py-3.5 pl-3" onClick={() => setDisplayModalConfirmation(!displayModalConfirmation)}>
						<CompanyLogo />
					</section>
					{token.length > 0 && (
						<section className="ml-auto flex shrink-0">
							<Button variant="ghost" size="icon-lg" onClick={() => setDisplayModalConfirmation(!displayModalConfirmation)}>
								<Home className="size-6 text-[#dedede]" />
								<span className="sr-only">Return to dashboard</span>
							</Button>
							<Button variant="ghost" size="icon-lg" onClick={() => setMenuOpen(true)}>
								<Menu className="size-6 text-[#dedede]" />
								<span className="sr-only">Open account menu</span>
							</Button>
							<Sheet open={menuOpen} onOpenChange={setMenuOpen}>
								<SheetContent className="w-[min(20rem,calc(100vw-3rem))]" side="right">
									<SheetHeader>
										<SheetTitle className="user-name">{loggedInUserFullName}</SheetTitle>
										<SheetDescription className="user-email">{loggedInUserDetails.email}</SheetDescription>
									</SheetHeader>
									<Separator />
									<Button
										variant="ghost"
									className="logout-button"
									onClick={() => {
										handleClose();
										logout();
									}}
								>
										<LogOut className="mr-2 size-5" />
									Logout
									</Button>
								</SheetContent>
							</Sheet>
						</section>
					)}
				</div>
			)}
      {displayModalConfirmation && (
				<ModalConfirmation
					displayModalConfirmation={displayModalConfirmation}
					setDisplayModalConfirmation={setDisplayModalConfirmation}
					optionStore={optionStore}
					callBackProceedMethodProp={navigateToOrigin}
				/>
			)}
		</div>
	);
}
